import { logger } from "@/utils/logger";
/**
 * URL Validation Utility
 * Prevents open redirect vulnerabilities by validating URLs before redirection
 */

// Security configuration for URL validation
interface URLSecurityConfig {
  allowedDomains: string[];
  allowRelativePaths: boolean;
  logViolations: boolean;
}

const DEFAULT_CONFIG: URLSecurityConfig = {
  allowedDomains: [
    // Add trusted domains here as needed
    'localhost',
    '127.0.0.1',
    // Dealer domains can be added dynamically
  ],
  allowRelativePaths: true,
  logViolations: true,
};

/**
 * Validates if a URL is safe for redirection
 */
export const isValidRedirectURL = (url: string, config: Partial<URLSecurityConfig> = {}): boolean => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  if (!url || typeof url !== 'string') {
    logSecurityViolation('Invalid URL type', url, mergedConfig.logViolations);
    return false;
  }

  // Trim whitespace and decode any encoded characters
  const cleanUrl = url.trim();
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'ftp:'];
  if (dangerousProtocols.some(protocol => cleanUrl.toLowerCase().startsWith(protocol))) {
    logSecurityViolation('Dangerous protocol detected', cleanUrl, mergedConfig.logViolations);
    return false;
  }

  // Block URLs with suspicious patterns
  const suspiciousPatterns = [
    /^\s*javascript:/i,
    /data:\s*text\/html/i,
    /vbscript:/i,
    /%00/,  // Null bytes
    /%0a/i, // Line feed
    /%0d/i, // Carriage return
  ];
  
  if (suspiciousPatterns.some(pattern => pattern.test(cleanUrl))) {
    logSecurityViolation('Suspicious URL pattern detected', cleanUrl, mergedConfig.logViolations);
    return false;
  }

  // Allow relative paths if configured
  if (mergedConfig.allowRelativePaths && !cleanUrl.includes('://')) {
    // Additional validation for relative paths
    if (cleanUrl.startsWith('/') || cleanUrl.startsWith('./') || cleanUrl.startsWith('../')) {
      return true;
    }
    // Allow relative paths without leading slash (e.g., "page.html")
    if (!cleanUrl.includes(':')) {
      return true;
    }
  }

  // Validate absolute URLs
  try {
    const urlObj = new URL(cleanUrl);
    
    // Check protocol
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      logSecurityViolation('Invalid protocol', cleanUrl, mergedConfig.logViolations);
      return false;
    }

    // Check against allowed domains
    const hostname = urlObj.hostname.toLowerCase();
    const isAllowedDomain = mergedConfig.allowedDomains.some(domain => {
      // Exact match or subdomain match
      return hostname === domain.toLowerCase() || hostname.endsWith('.' + domain.toLowerCase());
    });

    if (!isAllowedDomain) {
      logSecurityViolation('Domain not in allowlist', cleanUrl, mergedConfig.logViolations);
      return false;
    }

    return true;
  } catch (error) {
    logSecurityViolation('URL parsing error', cleanUrl, mergedConfig.logViolations);
    return false;
  }
};

/**
 * Safely redirects to a URL after validation
 */
export const safeRedirect = (url: string, config: Partial<URLSecurityConfig> = {}): boolean => {
  if (isValidRedirectURL(url, config)) {
    try {
      window.location.href = url;
      return true;
    } catch (error) {
      console.error('❌ Redirect failed:', error);
      return false;
    }
  }
  
  logger.warn('🚫 Blocked unsafe redirect attempt to:', url);
  return false;
};

/**
 * Gets the current domain for dynamic allowlist configuration
 */
export const getCurrentDomain = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
};

/**
 * Creates a dealer-specific URL validation config
 */
export const createDealerURLConfig = (dealerDomain?: string): Partial<URLSecurityConfig> => {
  const allowedDomains = [...DEFAULT_CONFIG.allowedDomains];
  
  // Add current domain
  const currentDomain = getCurrentDomain();
  if (currentDomain && !allowedDomains.includes(currentDomain)) {
    allowedDomains.push(currentDomain);
  }
  
  // Add dealer domain if provided
  if (dealerDomain && !allowedDomains.includes(dealerDomain)) {
    allowedDomains.push(dealerDomain);
  }
  
  return {
    allowedDomains,
    allowRelativePaths: true,
    logViolations: true,
  };
};

/**
 * Logs security violations
 */
const logSecurityViolation = (reason: string, url: string, shouldLog: boolean): void => {
  if (!shouldLog) return;
  
  logger.warn(`🚫 URL Security Violation: ${reason}`, {
    url,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  });
  
  // Could be extended to send to security monitoring service
};